import { useEffect, useState } from "react";
import { Download, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { PageHeader, Loading, Stat, StatusBadge, Table, Empty } from "../../components/common";

export default function YearEnd() {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    API.get(`/year-end?year=${year}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [year]);

  return (
    <div data-testid="year-end-page">
      <PageHeader title="Year-End Business Tax Center" sub="Your annual tax position, packaged for your accountant."
        right={<div className="flex items-center gap-2">
          <select className="input-gov" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="year-select">
            {[now, now - 1, now - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <a className="btn-copper" href={`${process.env.REACT_APP_BACKEND_URL}/api/tax-package`} data-testid="download-package">
            <Download className="h-4 w-4" /> Download Year-End Tax Package
          </a>
        </div>} />

      {!data ? <Loading label="Compiling annual summary" /> : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Stat label="Annual Revenue" cents={data.annual_revenue_cents} tone="navy" testid="ye-revenue" />
            <Stat label="Taxable Revenue" cents={data.taxable_revenue_cents} tone="blue" testid="ye-taxable" />
            <Stat label="TPT Accrued" cents={data.tpt_accrued_cents} tone="gold" testid="ye-tpt-accrued" />
            <Stat label="TPT Paid" cents={data.tpt_paid_cents} tone="green" testid="ye-tpt-paid" />
            <Stat label="Outstanding Balance" cents={data.outstanding_balance_cents}
              tone={data.outstanding_balance_cents > 0 ? "red" : "green"} testid="ye-outstanding" />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 font-bold text-navy">Periods in {year}</h3>
              {data.periods.length === 0 ? <Empty title="No periods this year" /> : (
                <Table headers={["Period", "Liability", "Balance", "Status"]} testid="ye-periods-table">
                  {data.periods.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 font-semibold text-navy">{p.label}</td>
                      <td className="px-4 py-2.5 money">{moneyFmt(p.liability_cents)}</td>
                      <td className="px-4 py-2.5 money">{moneyFmt(p.balance_cents)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                    </tr>
                  ))}
                </Table>
              )}
            </div>
            <div>
              <h3 className="mb-3 flex items-center gap-2 font-bold text-navy"><PackageCheck className="h-4 w-4 text-gold" /> Tax Documents & Confirmations</h3>
              {data.documents.length === 0 ? <Empty title="No year-end documents yet" /> : (
                <div className="space-y-2">
                  {data.documents.map((d) => (
                    <div key={d.id} className="card-gov flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="font-semibold text-slate-700">{d.filename}</span>
                      <span className="text-xs text-slate-400">{d.folder}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs text-slate-400">The downloadable package includes sales summaries, reconciliations, tax calculations (with rule versions), payment confirmations, and notes.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function moneyFmt(c) {
  return "$" + ((c || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
